import Link from 'found/Link';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Button, Card, CardFooter, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';

import AdminMeterShowTariff from './AdminMeterShowTariff';
import AdminMeterShowTradeRule from './AdminMeterShowTradeRule';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactComponentElement} - AdminMeterShow component
 */
function AdminMeterShow(props) {
  if (!props) {
    return <Loading />;
  }

  const { meter } = props;
  const {
    uuid, externalIdentifier, createdAt, updatedAt, identifier, tradePointId,
    active, property, primaryBillingPoint, tariffs, rules,
  } = meter;
  const { start, finish } = active;
  const {
    uuid: propertyId, title: propertyTitle, active: propertyActive,
  } = property;
  const {
    uuid: primaryBillingPointId, identifier: primaryBillingPointIdentifier,
    active: primaryBillingPointActive,
  } = primaryBillingPoint;

  const trades = rules?.edges?.map((el) => (el.node)) || [];

  const intl = useIntl();

  // Page title
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_meter.admin_meter_show.page_title', defaultMessage: 'Enosi Admin - Meter - {identifier}' },
    { identifier },
  );

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.admin.breadcrumb', defaultMessage: 'Admin' });
  const breadcrumbMeters = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.meters.breadcrumb', defaultMessage: 'Meters' });
  const breadcrumbMeter = intl.formatMessage(
    { id: 'admin.admin_meter.admin_meter_show.meter.breadcrumb', defaultMessage: 'Meter: {identifier}' },
    { identifier },
  );

  // Card headers
  const headerMeterDetails = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.card.header.meter_details', defaultMessage: 'Meter details' });
  const headerTariffs = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.card.header.tariffs', defaultMessage: 'Tariffs' });
  const headerActiveTradeRules = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.card.header.active_trade_rules', defaultMessage: 'Active Trade Rules' });

  // Table headers - Meter details
  const headerActiveStatus = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.active_status', defaultMessage: 'Active status' });
  const headerId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.id', defaultMessage: 'ID' });
  const headerExtId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.external_identifier', defaultMessage: 'Ext ID' });
  const headerExtIdTooltip = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.external_identifier.tooltip', defaultMessage: 'External identifier' });
  const headerStart = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.start', defaultMessage: 'Start' });
  const headerFinish = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.finish', defaultMessage: 'Finish' });
  const headerIdentifier = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.identifier', defaultMessage: 'Identifier' });
  const headerTradePointId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.trade_point_id', defaultMessage: 'Trade Point ID' });
  const headerProperty = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.property', defaultMessage: 'Property' });
  const headerPrimaryBillingPoint = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.primary_billing_point', defaultMessage: 'Primary Billing Point' });
  const headerCreatedAt = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.created_at', defaultMessage: 'Created at' });
  const headerUpdatedAt = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.table.header.updated_at', defaultMessage: 'Updated at' });

  // Table headers - Tariffs
  const headerTariffId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.tariffs.table.header.id', defaultMessage: 'ID' });
  const headerTariffIdentifier = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.tariffs.table.header.identifier', defaultMessage: 'Identifier' });
  const headerTariffActive = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.tariffs.table.header.active', defaultMessage: 'Active' });
  const headerTariffMeter = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.tariffs.table.header.meter', defaultMessage: 'Meter' });

  // Table headers - Trade Rules
  const headerTradeRuleId = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.id.label', defaultMessage: 'ID' });
  const headerTradeRuleIdTitle = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.id.title', defaultMessage: 'identifier' });
  const headerTradeRuleColespanSeparator = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.colspan.separator.', defaultMessage: '/' });
  const headerTradeRuleDirection = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.direction.label', defaultMessage: 'Direction' });
  const headerTradeRuleType = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.type.label', defaultMessage: 'Type' });
  const headerTradeRuleState = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.state.label', defaultMessage: 'State' });
  const headerTradeRuleCounterpartyTrader = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.counterparty_trader', defaultMessage: 'Counterparty Trader' });
  const headerTradeRuleCounterpartyTradePoint = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.counterparty_trade_point', defaultMessage: 'Counterparty Trade Point' });
  const headerTradeRuleValidity = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.validity', defaultMessage: 'Validity' });

  const headerTradeRuleIdDirectionTypeState = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.trade_rules.table.header.id_direction_type_state.label', defaultMessage: '{headerTradeRuleId} {headerTradeRuleColespanSeparator} {headerTradeRuleDirection} {headerTradeRuleColespanSeparator} {headerTradeRuleType} {headerTradeRuleColespanSeparator} {headerTradeRuleState}' }, {
    headerTradeRuleId: <abbr title={headerTradeRuleIdTitle}>{headerTradeRuleId}</abbr>,
    headerTradeRuleColespanSeparator,
    headerTradeRuleDirection,
    headerTradeRuleType,
    headerTradeRuleState,
  });

  // Buttons
  const buttonViewAllTradeRules = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.button.view_all_trade_rules', defaultMessage: 'View all trade rules' });

  const headerClauses = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_show.header.clauses.label', defaultMessage: 'Clauses' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbMeters, path: '/admin/meters' },
          { name: breadcrumbMeter },
        ]}
      />

      <h1>
        {identifier}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {headerMeterDetails}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{headerActiveStatus}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerId}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title={headerExtIdTooltip}>{headerExtId}</abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerStart}</th>
                  <td>
                    {start && DateTime.fromSeconds(start).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerFinish}</th>
                  <td>
                    {finish && DateTime.fromSeconds(finish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerIdentifier}</th>
                  <td>{identifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerTradePointId}</th>
                  <td>
                    <UUID uuid={tradePointId} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerProperty}</th>
                  <td>
                    <Link to={`/admin/properties/${propertyId}`}>
                      <strong>{propertyTitle}</strong>
                    </Link>
                    <br />
                    <UUID uuid={propertyId} />
                    <br />
                    <BadgeActive start={propertyActive.start} finish={propertyActive.finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerPrimaryBillingPoint}</th>
                  <td>
                    <Link to={`/admin/primary-billing-points/${primaryBillingPointId}`}>
                      <strong>{primaryBillingPointIdentifier}</strong>
                    </Link>
                    <br />
                    <UUID uuid={primaryBillingPointId} />
                    <br />
                    <BadgeActive
                      start={primaryBillingPointActive.start}
                      finish={primaryBillingPointActive.finish}
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerCreatedAt}</th>
                  <td>
                    {createdAt
                        && DateTime.fromSeconds(createdAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{headerUpdatedAt}</th>
                  <td>
                    {updatedAt
                        && DateTime.fromSeconds(updatedAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card>
            <CardHeader>
              {headerTariffs}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{headerTariffId}</th>
                  <th scope="col">{headerTariffIdentifier}</th>
                  <th scope="col">{headerTariffActive}</th>
                  <th scope="col">{headerTariffMeter}</th>
                </tr>
              </thead>
              <tbody>
                {tariffs && tariffs.edges.map((el) => (
                  <AdminMeterShowTariff tariff={el.node} key={el.node?.uuid} />
                ))}
              </tbody>
            </Table>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              {headerActiveTradeRules}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">
                    {headerTradeRuleIdDirectionTypeState}
                  </th>
                  <th scope="col">{headerTradeRuleCounterpartyTrader}</th>
                  <th scope="col">{headerTradeRuleCounterpartyTradePoint}</th>
                  <th scope="col">{headerClauses}</th>
                  <th scope="col">{headerTradeRuleValidity}</th>
                </tr>
              </thead>
              <tbody>
                {trades
                    && trades.map((tradeRule) => (
                      <AdminMeterShowTradeRule
                        tradePointId={tradePointId}
                        rule={tradeRule}
                        propertyId={propertyId}
                        key={tradeRule.uuid}
                      />
                    ))}
              </tbody>
            </Table>
            <CardFooter>
              <Link to={`/admin/meters/${uuid}/trade-rules`}>
                <Button color="primary">
                  {buttonViewAllTradeRules}
                </Button>
              </Link>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminMeterShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminMeterShow.defaultProps = {
  meter: null,
};

export default createFragmentContainer(
  AdminMeterShow,
  {
    meter: graphql`
      fragment AdminMeterShow_meter on Meter {
        uuid
        externalIdentifier
        createdAt
        updatedAt
        identifier
        tradePointId
        active { start finish }
        property {
          uuid
          title
          active { start finish }
        }
        primaryBillingPoint {
          uuid
          identifier
          active { start finish }
        }
        tariffs(first: 500) {
          edges {
            node {
              uuid
              identifier
              active { start finish }
              meter {uuid}
            }
          }
        }
        rules(
          first: 10
          state: TRADE_RULE_STATE_ACCEPTED
        ) {
          edges {
            cursor
            node {
              uuid
              ...AdminMeterShowTradeRule_rule
            }
          }
        }
      }
    `,
  },
);
